import React, { useEffect, useState } from "react";
import { Table, Badge, Container, Spinner, Alert, Button, Form } from "react-bootstrap";

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch all orders from the backend
    const fetchOrders = async () => {
        try {
            const isStaticAdmin = localStorage.getItem("adminAuthenticated") === "true";
            const adminEmail = localStorage.getItem("adminEmail");
            
            console.log('🔍 Admin auth check:', { isStaticAdmin, adminEmail });
            
            // Prepare headers based on authentication type
            const headers = {
                'Content-Type': 'application/json'
            };
            
            if (isStaticAdmin) {
                // Try multiple methods to send static admin auth
                // Method 1: Custom headers
                headers['x-static-admin-email'] = adminEmail || 'admin@madhu.com';
                headers['x-static-admin-auth'] = 'true';
                
                // Method 2: Also use Authorization header as fallback
                headers['Authorization'] = `StaticAdmin ${adminEmail || 'admin@madhu.com'}`;
                
                console.log('✅ Sending static admin authentication:', headers);
            } else {
                // Use token-based authentication
                const token = localStorage.getItem("token");
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                    console.log('✅ Using token authentication');
                } else {
                    throw new Error('No authentication found. Please login again.');
                }
            }

            // Try with headers first, if that fails, try with query params
            let url = "https://madhusewingmachines.com/api/orders/all";
            if (isStaticAdmin) {
                // Add query parameters as backup
                url += `?staticAdminEmail=${encodeURIComponent(adminEmail || 'admin@madhu.com')}&staticAdminAuth=true`;
            }
            
            const response = await fetch(url, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: response.statusText }));
                console.error('❌ API Error:', errorData);
                throw new Error(errorData.message || `Failed to fetch: ${response.statusText}`);
            }

            const data = await response.json();
            setOrders(data.data || []);
        } catch (err) {
            console.error('❌ Fetch orders error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    // Update order status
    const updateOrderStatus = async (orderId, newStatus) => {
        try {
            const isStaticAdmin = localStorage.getItem("adminAuthenticated") === "true";
            const adminEmail = localStorage.getItem("adminEmail");
            
            // Prepare headers based on authentication type
            const headers = {
                "Content-Type": "application/json"
            };
            
            if (isStaticAdmin) {
                // Try multiple methods to send static admin auth
                headers['x-static-admin-email'] = adminEmail || 'admin@madhu.com';
                headers['x-static-admin-auth'] = 'true';
                headers['Authorization'] = `StaticAdmin ${adminEmail || 'admin@madhu.com'}`;
            } else {
                // Use token-based authentication
                const token = localStorage.getItem("token");
                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
            }

            // Add query parameters for static admin as backup
            let url = `https://madhusewingmachines.com/api/orders/${orderId}/status`;
            if (isStaticAdmin) {
                url += `?staticAdminEmail=${encodeURIComponent(adminEmail || 'admin@madhu.com')}&staticAdminAuth=true`;
            }

            const response = await fetch(url, {
                method: "PUT",
                headers: headers,
                body: JSON.stringify({ status: newStatus })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "Failed to update order status");
            }

            // Update the order in state
            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order._id === orderId ? { ...order, status: newStatus } : order
                )
            );
        } catch (error) {
            console.error("❌ Error updating order status:", error);
            setError(error.message || "Failed to update order status");
        }
    };

    if (loading) return <Spinner animation="border" className="mt-4" />;
    if (error) return <Alert variant="danger">Error: {error}</Alert>;

    return (
        <Container className="mt-4">
            <h2>Admin Orders</h2>
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Order #</th>
                        <th>Customer</th>
                        <th>Address</th>
                        <th>Products</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.map(order => (
                        <tr key={order._id}>
                            <td>{order.orderNumber ?? order._id}</td>
                            <td>{order.fullName}</td>
                            <td>{order.street}, {order.city}, {order.state}, {order.zip}</td>
                            <td>
                                {order.cartItems.map((item, index) => (
                                    <div key={index}>{item.name} (x{item.quantity})</div>
                                ))}
                            </td>
                            <td>₹{order.total}</td>
                            <td>
                                <Badge bg="info">{order.status}</Badge>
                            </td>
                            <td>
                                <Form.Select
                                    onChange={(e) => updateOrderStatus(order._id, e.target.value)}
                                    defaultValue={order.status}
                                >
                                    <option value="Pending">Pending</option>
                                    <option value="Processing">Processing</option>
                                    <option value="Shipped">Shipped</option>
                                    <option value="Delivered">Delivered</option>
                                    <option value="Cancelled">Cancelled</option>
                                </Form.Select>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </Container>
    );
};

export default Orders;
